import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { App, Drawer, Modal, Space, Tooltip, Typography } from "antd";
import { useShallow } from "zustand/react/shallow";
import BrandButton, {
  brandModalConfirmProps,
  modalWarningTone,
} from "@/components/BrandButton";
import WarningIcon from "@/assets/warning.svg?react";
import InfoIcon from "@/assets/info.svg?react";
import {
  getVersion,
  saveDraft,
  submitReview,
  updateSchedule,
} from "@/api/versions";
import { applyPendingCreateSpaceLock } from "@/lib/createSpaceLock";
import {
  useAppStore,
  useUploadStore,
  useVersionDrawerStore,
} from "@/store";
import type { AppVersion, Platform, PlatformPackage } from "@/types";
import { confirmVersionAction } from "../versionActions";
import {
  CardTitle,
  DrawerCloseExtra,
  SectionCard,
  SectionTitle,
} from "./parts";
import { applyMainUpload } from "./applyMainUpload";
import MainPackageSection from "./MainPackageSection";
import PlatformBuildsSection from "./PlatformBuildsSection";
import { useConfirmDropUploads } from "../useConfirmDropUploads";
import {
  canEditAppFixedInfo,
  formFromVersion,
  isFormDirty,
  mergeDisplayInfo,
  mergeAppFixedInfo,
  versionPatchFromForm,
  type VersionForm,
} from "./formModel";
import { validateVersionSubmit } from "./validateVersionSubmit";
import { isOptionalHttpsUrl } from "./httpsUrl";
import ReleaseNotesSection from "./ReleaseNotesSection";
import AppFixedInfoSection from "./AppFixedInfoSection";
import DisplayInfoSection from "./DisplayInfoSection";
import PermissionsSection, {
  permissionPanelMode,
} from "./PermissionsSection";
import EventsSection, { eventPanelMode } from "./EventsSection";
import ReleaseScheduleSection from "./ReleaseScheduleSection";
import ScheduledOpsBar from "./ScheduledOpsBar";
import {
  hasEverReleased,
  isBackendHistoricalPackage,
  maxEverReleasedVersion,
  platformLabel,
} from "../platform";
import { refreshAppVersions } from "../refreshAppVersions";

const EDITABLE = new Set(["developing", "rejected"]);

const EMPTY_VERSIONS: AppVersion[] = [];

/** 重传之后另拉一次详情，只补表单字段，不把主包 / 子包盖回上传前 */
function fillFormFieldsFromDetail(
  current: AppVersion,
  detail: AppVersion,
): AppVersion {
  const display_info = mergeDisplayInfo(
    detail.display_info,
    current.display_info,
  );
  const app_fixed_info = mergeAppFixedInfo(
    detail.app_fixed_info,
    current.app_fixed_info,
  );
  return {
    ...current,
    // 详情没带定时发布时间时不要把本地已经排好的清掉
    scheduled_at: detail.scheduled_at ?? current.scheduled_at,
    ...(detail.changelog ? { changelog: detail.changelog } : {}),
    ...(display_info ? { display_info } : {}),
    ...(app_fixed_info ? { app_fixed_info } : {}),
    ...(detail.permission_diff
      ? { permission_diff: detail.permission_diff }
      : {}),
    ...(detail.event_diff ? { event_diff: detail.event_diff } : {}),
  };
}

export default function CreateVersionDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [askUnsaved, setAskUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const { open, app_id, version_id, close } = useVersionDrawerStore(
    useShallow((s) => ({
      open: s.open,
      app_id: s.app_id,
      version_id: s.version_id,
      close: s.close,
    })),
  );

  const confirmDropUploads = useConfirmDropUploads();
  const {
    clearUpload,
    releaseUpload,
    hasUploading,
    mainUploading,
    mainParse,
    uploadDraft,
    draft_version_id,
    mainTaskStatus,
  } = useUploadStore(
    useShallow((s) => ({
      clearUpload: s.clear,
      releaseUpload: s.releaseIfIdle,
      // 只看本版本的任务，别的草稿在传不挡这里提交
      hasUploading: s.tasks.some(
        (x) =>
          x.version_id === version_id &&
          (x.status === "uploading" || x.status === "parsing"),
      ),
      mainUploading: s.tasks.some(
        (x) =>
          x.kind === "main" &&
          x.version_id === version_id &&
          (x.status === "uploading" || x.status === "parsing"),
      ),
      mainParse: s.mainParse,
      uploadDraft: s.draft,
      draft_version_id: s.draft_version_id,
      mainTaskStatus:
        s.tasks.find(
          (x) => x.kind === "main" && x.version_id === version_id,
        )?.status ?? null,
    })),
  );

  const app = useAppStore((s) => (app_id ? s.getApp(app_id) : undefined));
  const versions = useAppStore((s) =>
    app_id ? (s.versions[app_id] ?? EMPTY_VERSIONS) : EMPTY_VERSIONS,
  );
  const ensureRemoteDraft = useAppStore((s) => s.ensureRemoteDraft);
  const maxOnlineVersion = useAppStore((s) =>
    app_id ? s.maxOnlineVersion[app_id] : undefined,
  );
  const store = useAppStore(
    useShallow((s) => ({
      saveVersion: s.saveVersion,
      submitVersion: s.submitVersion,
      deleteVersion: s.deleteVersion,
      withdrawVersion: s.withdrawVersion,
      cancelSchedule: s.cancelSchedule,
      releaseNow: s.releaseNow,
      delistVersion: s.delistVersion,
      disableVersion: s.disableVersion,
      enableVersion: s.enableVersion,
      relistVersion: s.relistVersion,
      rollbackVersion: s.rollbackVersion,
    })),
  );

  const version = version_id
    ? versions.find((v) => v.id === version_id)
    : undefined;

  const [form, setForm] = useState<VersionForm | null>(null);
  const formRef = useRef<VersionForm | null>(null);
  formRef.current = form;
  const boundId = useRef<string | null>(null);
  // 重传主包开始后，作废打开抽屉时那次详情请求
  const detailToken = useRef(0);
  useEffect(() => {
    if (!open) detailToken.current = 0;
  }, [open]);

  // 打开配置抽屉时拉远端详情（竖切第一刀）
  useEffect(() => {
    if (!open || !app_id || !version_id) return;
    const token = detailToken.current;
    let cancelled = false;
    setDetailLoading(true);
    void getVersion(app_id, version_id)
      .then((detail) => {
        if (cancelled || detailToken.current !== token) return;
        const local = useAppStore
          .getState()
          .versions[app_id]?.find((v) => v.id === version_id);
        const app_fixed_info = mergeAppFixedInfo(
          detail.app_fixed_info,
          local?.app_fixed_info,
        );
        const display_info = mergeDisplayInfo(
          detail.display_info,
          local?.display_info,
        );
        const merged = applyPendingCreateSpaceLock(app_id, {
          ...detail,
          ...(app_fixed_info ? { app_fixed_info } : {}),
          ...(display_info ? { display_info } : {}),
        });
        ensureRemoteDraft({
          app_id,
          appName: app?.name,
          icon: detail.main_package?.icon ?? app?.icon,
          version: merged,
        });
        setForm(formFromVersion(merged));
        boundId.current = merged.id;
      })
      .catch(() => {
        // 后端不可用时回退本地缓存
        if (cancelled || detailToken.current !== token || !version) return;
        const locked = applyPendingCreateSpaceLock(app_id, version);
        if (locked !== version) {
          ensureRemoteDraft({
            app_id,
            appName: app?.name,
            icon: version.main_package?.icon ?? app?.icon,
            version: locked,
          });
        }
        setForm(formFromVersion(locked));
        boundId.current = version.id;
      })
      .finally(() => {
        // token 已变说明重传开始了，这次旧详情不能结束 loading，否则会把旧表单钉住
        if (cancelled || detailToken.current !== token) return;
        setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // 仅在打开目标变化时拉详情
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, app_id, version_id]);

  useEffect(() => {
    if (version && app_id && boundId.current !== version.id && !detailLoading) {
      const locked = applyPendingCreateSpaceLock(app_id, version);
      if (locked !== version) {
        ensureRemoteDraft({
          app_id,
          appName: app?.name,
          icon: version.main_package?.icon ?? app?.icon,
          version: locked,
        });
      }
      setForm(formFromVersion(locked));
      boundId.current = locked.id;
    }
    if (!version) {
      setForm(null);
      boundId.current = null;
    }
  }, [app?.name, app_id, detailLoading, ensureRemoteDraft, version]);

  // 认这一次响应对象，同名同版本号再传也会写入
  const appliedMain = useRef<typeof mainParse>(null);
  useEffect(() => {
    if (!open || !app_id || !version_id || !mainParse || !uploadDraft) return;
    if (draft_version_id !== version_id) return;
    if (appliedMain.current === mainParse) return;
    appliedMain.current = mainParse;
    const local = useAppStore
      .getState()
      .versions[app_id]?.find((v) => v.id === version_id);
    const next = applyPendingCreateSpaceLock(
      app_id,
      applyMainUpload(local, uploadDraft, mainParse, version_id),
    );
    ensureRemoteDraft({
      app_id,
      icon: mainParse.icon,
      replacePlatformPackages: true,
      version: next,
    });
    useAppStore.getState().bumpVersionsListRev();
    const merged =
      useAppStore.getState().versions[app_id]?.find((v) => v.id === version_id) ??
      next;
    const dirty = form && local ? isFormDirty(form, local) : false;
    if (!dirty) {
      setForm(formFromVersion(merged));
      boundId.current = merged.id;
    }
    setDetailLoading(false);
    // 打开抽屉时那次详情已经作废。这里再拉一次，只补展示字段。
    const token = detailToken.current;
    void getVersion(app_id, version_id)
      .then((detail) => {
        if (detailToken.current !== token) return;
        const viewing = useVersionDrawerStore.getState();
        const stillHere =
          viewing.open &&
          viewing.app_id === app_id &&
          viewing.version_id === version_id;
        const current = useAppStore
          .getState()
          .versions[app_id]?.find((v) => v.id === version_id);
        if (!current) return;
        const latest = formRef.current;
        if (stillHere && latest && isFormDirty(latest, current)) return;
        const filled = fillFormFieldsFromDetail(current, detail);
        ensureRemoteDraft({
          app_id,
          icon: filled.main_package?.icon ?? mainParse.icon,
          version: {
            ...filled,
            // 只把这次详情里的子包交给合并；已替换过的版本会留下本地列表，并补上历史包
            platform_packages: detail.platform_packages,
          },
        });
        if (!stillHere) return;
        setForm(formFromVersion(filled));
        boundId.current = filled.id;
      })
      .catch(() => {
        // 包信息已经落本地；详情补不齐就保持刚才的合并结果
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, app_id, version_id, mainParse, uploadDraft, draft_version_id]);

  // 重传失败或取消后，旧详情已经作废，放开 loading 让表单回到当前草稿
  useEffect(() => {
    if (!open || !version_id || !detailLoading) return;
    if (detailToken.current === 0) return;
    if (
      mainTaskStatus === "uploading" ||
      mainTaskStatus === "parsing" ||
      mainTaskStatus === "success"
    ) {
      return;
    }
    setDetailLoading(false);
  }, [open, version_id, detailLoading, mainTaskStatus]);

  if (!app || !version_id || !version) return null;

  const status = version.status;
  const editable = EDITABLE.has(status);
  const dirty = form ? isFormDirty(form, version) : false;
  const fixedEditable = canEditAppFixedInfo(editable, version.app_fixed_info);

  const maxReleased = maxOnlineVersion || maxEverReleasedVersion(versions);

  const declaredPlatforms =
    version.main_package?.platforms ?? version.platforms ?? [];

  const persistPlatformPackages = (next: PlatformPackage[]) => {
    if (!app_id) return;
    // 版本支持平台以主包声明为准，不随勾选/历史包条目收缩
    store.saveVersion(app_id, version.id, {
      platform_packages: next,
      platforms: declaredPlatforms.length
        ? declaredPlatforms
        : next.map((p) => p.platform),
    });
  };

  // 沿用历史包只认后端（parse-result historical_packages / 详情 is_historical）
  const historicalByPlatform = Object.fromEntries(
    declaredPlatforms.map((p) => [
      p,
      (version.platform_packages ?? []).find(
        (pkg) => pkg.platform === p && isBackendHistoricalPackage(pkg),
      ),
    ]),
  ) as Partial<Record<Platform, PlatformPackage>>;

  const update = (patch: Partial<VersionForm>) =>
    setForm((f) => (f ? { ...f, ...patch } : f));

  const urlInvalid = !!form && (
    !isOptionalHttpsUrl(form.display_info.website_url) ||
    !isOptionalHttpsUrl(form.display_info.privacy_policy_url) ||
    !isOptionalHttpsUrl(form.display_info.terms_url)
  );

  const doSave = async (): Promise<boolean> => {
    if (!form || !app_id) return false;
    if (urlInvalid) {
      message.error(t("versionForm.errUrl"));
      return false;
    }
    const patch = versionPatchFromForm(form, {
      is_first_version: !!version.is_first_version,
    });
    if (patch.display_info) {
      setForm((f) => (f ? { ...f, display_info: patch.display_info! } : f));
    }
    setSaving(true);
    try {
      await saveDraft(app_id, version.id, patch);
      store.saveVersion(app_id, version.id, patch);
      message.success(t("versions.saveSuccess"));
      return true;
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submitError = validateVersionSubmit({
    version,
    form,
    maxReleased,
    hasUploading,
    siblingVersions: versions,
  });
  const submitErrorText = submitError
    ? t(`versionForm.${submitError.code}`, {
        max: submitError.max,
        platforms: submitError.platforms
          ?.map(platformLabel)
          .join(" / "),
      })
    : null;

  const showIssuedCredentials = (credentials: {
    app_id: string;
    app_secret: string;
  }) => {
    modal.info({
      title: t("versions.issuedCredentialsTitle"),
      width: 480,
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>{t("versions.issuedCredentialsBody")}</div>
          <div>
            {t("credentials.appId")}:{" "}
            <Typography.Text copyable>{credentials.app_id}</Typography.Text>
          </div>
          <div>
            {t("credentials.appSecret")}:{" "}
            <Typography.Text copyable>{credentials.app_secret}</Typography.Text>
          </div>
        </div>
      ),
    });
  };

  const doSubmit = async () => {
    if (!form || !app_id) return;
    if (submitErrorText) {
      message.error(submitErrorText);
      return;
    }
    const patch = versionPatchFromForm(form, {
      is_first_version: !!version.is_first_version,
    });
    if (patch.display_info) {
      setForm((f) => (f ? { ...f, display_info: patch.display_info! } : f));
    }
    const scheduled_at = form.releaseImmediate ? null : form.scheduled_at;
    setSubmitting(true);
    try {
      await saveDraft(app_id, version.id, patch);
      const result = await submitReview(app_id, version.id, scheduled_at);
      store.saveVersion(app_id, version.id, patch);
      if (result.status === "released") {
        // 免审直发会顶替线上版本：等整表刷新，不乐观改行
        await refreshAppVersions(app_id);
      } else {
        store.submitVersion(app_id, version.id, result);
      }
      message.success(
        t(
          result.status === "released"
            ? "versions.submitReleased"
            : result.status === "scheduled"
              ? "versions.submitScheduled"
              : "versions.submitSuccess",
        ),
      );
      if (result.credentials) showIssuedCredentials(result.credentials);
      clearUpload();
      close();
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const finishClose = () => {
    // 主包不进浮层：重传中关闭需确认后中断
    if (mainUploading) {
      modal.confirm({
        title: t("versionForm.closeWhileUploadingTitle"),
        content: t("versionForm.closeWhileUploadingContent"),
        okText: t("versionForm.closeWhileUploadingOk"),
        cancelText: t("common.cancel"),
        ...brandModalConfirmProps("danger"),
        onOk: () => {
          clearUpload();
          close();
        },
      });
      return;
    }
    // 子包没传完 / 失败时保留会话，交给右下角浮层
    releaseUpload();
    close();
  };

  const handleClose = () => {
    if (editable && dirty) {
      setAskUnsaved(true);
      return;
    }
    finishClose();
  };

  const goScopeManage = () => {
    navigate(`/apps/${app_id}/scope`);
    handleClose();
  };

  const runAction = (
    action: Parameters<typeof confirmVersionAction>[0]["action"],
  ) => {
    if (!app_id) return;
    confirmVersionAction({
      t,
      modal,
      message,
      action,
      version,
      app_id,
      store,
      appVersions: versions,
      onDone: () => {
        if (action === "delete") {
          clearUpload();
          close();
        }
      },
    });
  };

  const header = (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <span style={{ fontWeight: 500, fontSize: 18, flex: 1, lineHeight: "25px" }}>
        {`v${version.version}`}
      </span>
    </div>
  );

  function renderActions() {
    if (!version) return null
    switch (version.status) {
      case "developing":
      case "rejected":
        return (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <BrandButton
              variant="danger"
              onClick={() => runAction("delete")}
            >
              {t("versionForm.deleteVersion")}
            </BrandButton>
            <Space size={10}>
              <Tooltip
                title={
                  urlInvalid ? t("versionForm.errUrl") : undefined
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <BrandButton
                    variant="outline"
                    disabled={!dirty || saving || submitting || urlInvalid}
                    loading={saving}
                    onClick={() => void doSave()}
                  >
                    {t("versionForm.saveVersion")}
                  </BrandButton>
                </span>
              </Tooltip>
              <Tooltip title={submitErrorText ?? undefined}>
                <span style={{ display: "inline-flex" }}>
                  <BrandButton
                    disabled={!!submitErrorText || saving || submitting}
                    loading={submitting}
                    onClick={() => void doSubmit()}
                  >
                    {t("versionForm.submitReview")}
                  </BrandButton>
                </span>
              </Tooltip>
            </Space>
          </Space>
        );
      case "reviewing":
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <BrandButton
              variant="outline"
              onClick={() => runAction("withdraw")}
            >
              {t("versions.action_withdraw")}
            </BrandButton>
          </Space>
        );
      case "delisted":
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <BrandButton onClick={() => runAction("relist")}>
              {t("versions.action_relist")}
            </BrandButton>
          </Space>
        );
      case "disabled":
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <BrandButton onClick={() => runAction("enable")}>
              {t("versions.action_enable")}
            </BrandButton>
          </Space>
        );
      case "archived":
        if (!hasEverReleased(version)) {
          return (
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <BrandButton onClick={() => runAction("releaseNow")}>
                {t("versions.action_releaseNow")}
              </BrandButton>
            </Space>
          );
        }
        return null;
      default:
        return null;
    }
  }

  const showForm = !!form;
  const drawerBg = "var(--color-background-module-muted)";
  const main = version.main_package;

  const topActions = version.status === "scheduled" ? null : renderActions();

  const scheduledOps = version.status === "scheduled" && (
    <ScheduledOpsBar
      version={version}
      onCancel={() => runAction("cancelSchedule")}
      onReleaseNow={() => runAction("releaseNow")}
      onModify={async (iso: string) => {
        if (!app_id) throw new Error("Missing app_id");
        try {
          await updateSchedule(app_id, version.id, iso);
          store.saveVersion(app_id, version.id, { scheduled_at: iso });
          message.success(t("versions.scheduleUpdated"));
        } catch (err: unknown) {
          message.error(err instanceof Error ? err.message : String(err));
          throw err;
        }
      }}
    />
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={614}
      title={header}
      closable={false}
      extra={<DrawerCloseExtra onClose={handleClose} />}
      maskClosable={!editable || !dirty}
      footer={null}
      styles={{
        body: {
          padding: 0,
          background: drawerBg,
          display: "flex",
          flexDirection: "column",
        },
        content: {
          background: drawerBg,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0px 16px 22px -6px rgba(28, 28, 28, 0.16)",
        },
        header: {
          background: drawerBg,
          borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
          padding: "20px",
        },
      }}
    >
      {topActions && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            padding: "14px 20px",
            background: "var(--color-background-module-muted)",
            backdropFilter: "blur(6px)",
            borderBottom: "1px solid var(--color-border-line)",
          }}
        >
          {topActions}
        </div>
      )}

      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {scheduledOps}

        {version.status === "rejected" && (
          <div
            style={{
              display: "flex",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 12,
              background: "rgba(255,56,60,0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 22,
                flexShrink: 0,
                color: "#E53E3E",
              }}
            >
              <InfoIcon width={16} height={16} />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  color: "#E53E3E",
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: "22px",
                }}
              >
                {t("versionForm.rejectedTitle")}
              </div>
              <div
                style={{
                  color: "#000",
                  fontSize: 13,
                  lineHeight: "20px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {version.review_comment || t("versionForm.rejectedNoReason")}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SectionTitle>{t("versionForm.mainPackage")}</SectionTitle>
          <MainPackageSection
            app={app}
            committed={main}
            readOnly={!editable}
            uploadContext={{ app_id: app_id!, version_id: version.id }}
            beforeStart={(proceed) =>
              confirmDropUploads(() => {
                detailToken.current += 1;
                proceed();
              })
            }
          />
        </div>

        {open && editable && main?.needs_platform_packages && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <SectionTitle>{t("versionForm.platformBuilds")}</SectionTitle>
            <PlatformBuildsSection
              key={`${version.id}:${main.file_name}:${main.version_code}`}
              main_package={main}
              value={version.platform_packages ?? []}
              readOnly={false}
              isFirstVersion={!!version.is_first_version}
              historicalByPlatform={historicalByPlatform}
              onChange={persistPlatformPackages}
              uploadContext={{ app_id: app_id!, version_id: version.id }}
            />
          </div>
        )}

        {showForm && (
          <>
            {!version.is_first_version && (
              <SectionCard>
                <CardTitle required>{t("versionForm.releaseNotes")}</CardTitle>
                <ReleaseNotesSection
                  changelog={form!.changelog}
                  supported_languages={form!.display_info.supported_languages}
                  default_language={form!.display_info.default_language}
                  editable={editable}
                  onChange={(changelog) => update({ changelog })}
                  platform_packages={
                    version.main_package?.needs_platform_packages
                      ? version.platform_packages
                      : undefined
                  }
                />
              </SectionCard>
            )}

            <SectionCard>
              <CardTitle>{t("versionForm.appFixedInfo")}</CardTitle>
              <AppFixedInfoSection
                value={form!.app_fixed_info}
                editable={fixedEditable}
                onChange={(app_fixed_info) => update({ app_fixed_info })}
              />
            </SectionCard>

            <SectionCard>
              <CardTitle>{t("versionForm.displayInfo")}</CardTitle>
              <DisplayInfoSection
                value={form!.display_info}
                editable={editable}
                app_id={app_id!}
                version_id={version.id}
                runtimes={version.main_package?.runtimes}
                onChange={(display_info) => update({ display_info })}
              />
            </SectionCard>

            {version.permission_diff &&
            permissionPanelMode(version.permission_diff) !== "none" ? (
              <SectionCard>
                <CardTitle>{t("versionForm.permissions")}</CardTitle>
                <PermissionsSection
                  key={version.id}
                  diff={version.permission_diff}
                  onGoManage={goScopeManage}
                />
              </SectionCard>
            ) : null}

            {version.event_diff &&
            eventPanelMode(version.event_diff) !== "none" ? (
              <SectionCard>
                <CardTitle>{t("versionForm.events")}</CardTitle>
                <EventsSection
                  key={`events:${version.id}`}
                  diff={version.event_diff}
                />
              </SectionCard>
            ) : null}

            {version.status !== "scheduled" && (
              <SectionCard>
                <CardTitle>{t("versionForm.releaseSchedule")}</CardTitle>
                <ReleaseScheduleSection
                  immediate={form!.releaseImmediate}
                  scheduled_at={form!.scheduled_at}
                  editable={editable}
                  onChange={(immediate, scheduled_at) =>
                    update({ releaseImmediate: immediate, scheduled_at })
                  }
                />
              </SectionCard>
            )}
          </>
        )}
      </div>

      <Modal
        open={!!askUnsaved}
        title={
          <Space size={20} align="center">
            <WarningIcon
              className="size-7"
              style={{ color: modalWarningTone.primary }}
            />
            <span>{t("versionForm.unsavedTitle")}</span>
          </Space>
        }
        onCancel={() => setAskUnsaved(false)}
        footer={
          <Space>
            <BrandButton
              variant="outline"
              onClick={() => setAskUnsaved(false)}
            >
              {t("common.cancel")}
            </BrandButton>
            <BrandButton
              variant="outline"
              onClick={() => {
                finishClose();
                setAskUnsaved(false);
              }}
            >
              {t("versionForm.dontSave")}
            </BrandButton>
            <BrandButton
              disabled={urlInvalid || saving || submitting}
              onClick={() => {
                void doSave().then((ok) => {
                  if (!ok) return;
                  finishClose();
                  setAskUnsaved(false);
                });
              }}
            >
              {t("versionForm.saveChanges")}
            </BrandButton>
          </Space>
        }
        styles={{
          body: {
            paddingLeft: "48px",
          },
        }}
      >
        {t("versionForm.unsavedBody")}
      </Modal>
    </Drawer>
  );
}
